import {
  OldCustomField,
  OldCustomFieldType,
  OldFieldValueType,
} from '@teetsh/app/src/types/oldCustomField';
import _isArray from 'lodash-es/isArray';
import _uniq from 'lodash-es/uniq';
import { normalize } from '@teetsh/app/src/utils/normalize';
import {
  CustomFieldDefinition,
  CustomFieldOption,
  FieldType,
} from 'types/customField';
import { convertOptionsFromSourceToTarget } from './convertOptionsFromSourceToTarget';
import { isDefined } from 'types/utils';

interface ImportCustomFieldResult {
  updatedCustomFields: OldCustomField[];
  missingCustomFieldOptions: Record<string, CustomFieldOption[]>;
}

function isSameDefinitionByTypeAndName(
  definition: CustomFieldDefinition,
  otherDefinition: CustomFieldDefinition,
): boolean {
  return (
    definition.type === otherDefinition.type &&
    normalize(definition.name) === normalize(otherDefinition.name)
  );
}

function matchingSupportedFieldType(
  type: FieldType,
): OldCustomFieldType | undefined {
  if (type === 'select') return 'multitag'; // important : multitag and select are the same, but we need to map it for retrocompatibility
  if (type === 'multiselect') return 'multiselect';
  if (type === 'text') return 'text';
  // other types are not supported yet

  return undefined;
}

function mergeValue(
  currValue: OldFieldValueType,
  newValue: OldFieldValueType,
): OldFieldValueType {
  if (!_isArray(currValue) || !_isArray(newValue)) {
    // return existing value as-is if it's not an array
    return currValue;
  }
  // otherwise, merge the values
  return _uniq([...currValue, ...newValue]);
}

export function getTargetCustomFieldsFromSource(
  sourceCustomFieldValues: OldCustomField[],
  sourceCustomFieldDefinitions: CustomFieldDefinition[],
  targetCustomFieldValues: OldCustomField[],
  targetCustomFieldDefinitions: CustomFieldDefinition[],
): ImportCustomFieldResult {
  return sourceCustomFieldValues.reduce<ImportCustomFieldResult>(
    (acc, sourceCustomField) => {
      const matchingSourceDefinition = sourceCustomFieldDefinitions.find(
        def => def.id === sourceCustomField.id,
      );
      if (!matchingSourceDefinition) {
        return acc;
      }
      const matchingTargetDefinition = targetCustomFieldDefinitions.find(def =>
        isSameDefinitionByTypeAndName(def, matchingSourceDefinition),
      );
      if (!matchingTargetDefinition) {
        return acc;
      }
      const compatibleFieldType = matchingSupportedFieldType(
        matchingTargetDefinition.type,
      );
      if (!compatibleFieldType) {
        return acc;
      }
      const matchingTargetField = acc.updatedCustomFields.find(
        targetField => targetField.id === matchingTargetDefinition.id,
      );
      const { missingOptions, sourceIdToTargetIdMap } =
        convertOptionsFromSourceToTarget(
          matchingSourceDefinition,
          matchingTargetDefinition,
          sourceCustomField.value,
        );

      // Map the source values to target values using the ID map
      const mappedValue = _isArray(sourceCustomField.value)
        ? sourceCustomField.value
            .map(id => sourceIdToTargetIdMap.get(id))
            .filter(isDefined)
        : sourceCustomField.value;

      if (!matchingTargetField) {
        // if not found, add field to the list
        const newCustomField: OldCustomField = {
          id: matchingTargetDefinition.id,
          items: sourceCustomField.items,
          singularName: matchingTargetDefinition.name,
          pluralName: matchingTargetDefinition.pluralName,
          value: mappedValue,
          type: compatibleFieldType,
        };

        return {
          ...acc,
          updatedCustomFields: [...acc.updatedCustomFields, newCustomField],
          missingCustomFieldOptions: {
            ...acc.missingCustomFieldOptions,
            ...(missingOptions.length > 0
              ? { [matchingTargetDefinition.id]: missingOptions }
              : {}),
          },
        };
      }

      // if found, return acc with updated field
      return {
        ...acc,
        updatedCustomFields: acc.updatedCustomFields.map(targetField => {
          if (targetField.id === matchingTargetField.id) {
            return {
              ...targetField,
              value: mergeValue(targetField.value, mappedValue),
            };
          }
          return targetField;
        }),
        missingCustomFieldOptions: {
          ...acc.missingCustomFieldOptions,
          ...(missingOptions.length > 0
            ? { [matchingTargetDefinition.id]: missingOptions }
            : {}),
        },
      };
    },
    {
      updatedCustomFields: targetCustomFieldValues,
      missingCustomFieldOptions: {},
    },
  );
}
